import React, { useState } from 'react';
import { TREATMENT_AREAS } from '../data/treatmentAreas';
import { TreatmentArea, BodyRegionCategory } from '../types';
import { Clock, Shield, Sparkles, CheckCircle2, Flame, Zap, Activity, Compass, Award, Info, ChevronDown, ChevronUp, Calculator, MessageSquare, ArrowRight, Heart } from 'lucide-react';
import { SpecialistAvatar } from './SpecialistAvatar';
import { useSite } from '../context/SiteContext';

interface TreatmentAreasProps {
  onSelectAreaForCalc?: (areaName: string) => void;
}

export const TreatmentAreas: React.FC<TreatmentAreasProps> = ({ onSelectAreaForCalc }) => {
  let isFemale = false;
  let currentAreas = TREATMENT_AREAS;

  try {
    const site = useSite();
    if (site) {
      isFemale = site.isFemale;
      currentAreas = site.treatmentAreas;
    }
  } catch {}

  const [activeCategory, setActiveCategory] = useState<BodyRegionCategory>('all');
  const [selectedAreaModal, setSelectedAreaModal] = useState<TreatmentArea | null>(null);
  const [showAllAreas, setShowAllAreas] = useState(false);

  const categories: { id: BodyRegionCategory; label: string }[] = isFemale
    ? [
        { id: 'all', label: 'Tüm Bölgeler' },
        { id: 'lower', label: 'Tüm Bacak & Genital & Bikini' },
        { id: 'arms', label: 'Koltuk Altı & Tüm Kol' },
        { id: 'face', label: 'Yüz & Bıyık & Çene' },
        { id: 'upper', label: 'Sırt & Göğüs & Göbek' },
        { id: 'packages', label: 'Full Vücut Avantaj Paketleri' },
      ]
    : [
        { id: 'all', label: 'Tüm Bölgeler' },
        { id: 'face', label: 'Yüz & Sakal & Ense' },
        { id: 'upper', label: 'Sırt & Göğüs & Omuz' },
        { id: 'arms', label: 'Kol & Koltuk Altı' },
        { id: 'lower', label: 'Alt Vücut & Özel Bölge' },
        { id: 'packages', label: 'Avantajlı Kombin Paketler' },
      ];

  const allFilteredAreas = activeCategory === 'all'
    ? currentAreas
    : currentAreas.filter(area => area.category === activeCategory);

  const visibleAreas = (activeCategory === 'all' && !showAllAreas)
    ? allFilteredAreas.slice(0, 6)
    : allFilteredAreas;

  const getIcon = (name: string) => {
    switch (name) {
      case 'Sparkles': return <Sparkles className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      case 'Shield': return <Shield className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      case 'Flame': return <Flame className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      case 'Zap': return <Zap className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      case 'Activity': return <Activity className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      case 'Compass': return <Compass className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      case 'Award': return <Award className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
      default: return <Sparkles className={`w-4 h-4 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />;
    }
  };

  return (
    <section id="bolgeler" className="py-12 sm:py-16 bg-white border-t border-slate-200 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold shadow-2xs ${
            isFemale ? 'bg-rose-50 border border-rose-200 text-rose-900' : 'bg-amber-50 border border-amber-300 text-amber-900'
          }`}>
            <Sparkles className={`w-3.5 h-3.5 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
            <span>{isFemale ? 'Kadın Ten & Kıl Yapısına Özel Hassas Protokoller' : 'Erkek Anatomisi & Kıl Köküne Özel Protokoller'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {isFemale ? 'Bursa Kadın Lazer Epilasyon' : 'Bursa Erkek Lazer Epilasyon'}{' '}
            <span className={isFemale ? 'text-rose-600' : 'text-amber-600'}>Uygulama Bölgeleri</span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            Fatih Sultan Mehmet Bulvarı kliniğimizde her bölge için {isFemale ? 'kadın cilt hassasiyetine' : 'erkek kıl yapısına'} özel dalga boyu ve safir soğutmalı buz başlık kullanılır.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center justify-start sm:justify-center gap-1.5 overflow-x-auto pb-3 mb-6 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat.id}
              id={`cat-btn-${cat.id}`}
              onClick={() => {
                setActiveCategory(cat.id);
                setShowAllAreas(true);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-700 hover:text-slate-900 hover:bg-slate-200/80 border border-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Treatment Cards Grid: Responsive 1 col (mobile), 2 cols (tablet), 3 cols (desktop) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {visibleAreas.map((area) => (
            <div
              key={area.id}
              id={`treatment-card-${area.id}`}
              className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between hover:border-amber-400 hover:shadow-md transition-all group"
            >
              <div>
                {/* Header with Icon & Category badge */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center group-hover:border-amber-400 transition-colors shrink-0">
                      {getIcon(area.iconName)}
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                      {area.name}
                    </h3>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 shrink-0">
                    {area.recommendedSessions}
                  </span>
                </div>

                {/* Short Description */}
                <p className="text-slate-600 text-xs mb-3 line-clamp-2 leading-relaxed">
                  {area.shortDesc}
                </p>

                {/* Duration & Pain Level Indicators */}
                <div className="grid grid-cols-2 gap-2 py-2 px-2.5 rounded-xl bg-slate-50 border border-slate-200/70 mb-4 text-[11px] font-medium">
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span>~{area.durationMinutes} dk / seans</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <Shield className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Buz Başlık (Acısız)</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons: 2 Focused, High-Conversion Actions */}
              <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedAreaModal(area)}
                  className="py-2 px-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 text-xs font-bold border border-slate-200 transition-colors flex items-center justify-center gap-1 cursor-pointer"
                  title={`${area.name} detaylarını inceleyin`}
                >
                  <Info className="w-3.5 h-3.5 text-slate-500" />
                  <span>Detay Gör</span>
                </button>

                <button
                  type="button"
                  id={`calc-btn-${area.id}`}
                  onClick={() => {
                    if (onSelectAreaForCalc) {
                      onSelectAreaForCalc(area.name);
                    } else {
                      document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="py-2 px-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-bold border border-amber-300 hover:border-amber-400 transition-colors flex items-center justify-center gap-1 shadow-2xs cursor-pointer group/btn"
                  title="Fiyat hesaplama listesine ekle"
                >
                  <Calculator className="w-3.5 h-3.5 text-amber-700" />
                  <span>Fiyat Hesapla</span>
                </button>
              </div>

            </div>
          ))}
        </div>

        {/* Expand / Collapse Toggle if in 'all' view */}
        {activeCategory === 'all' && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAllAreas(!showAllAreas)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold border border-slate-300 transition-all cursor-pointer"
            >
              {showAllAreas ? (
                <>
                  <span>Daha Az Bölge Göster</span>
                  <ChevronUp className="w-4 h-4 text-slate-600" />
                </>
              ) : (
                <>
                  <span>Tüm Bölgeleri Listele ({allFilteredAreas.length} Bölge)</span>
                  <ChevronDown className="w-4 h-4 text-amber-600" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Single Elegant Bottom Consultation Strip */}
        <div className="mt-10 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 border border-slate-800 p-4 sm:p-6 text-white flex flex-col md:flex-row items-center justify-between gap-4 shadow-md">
          <div className="flex items-center gap-3 text-center md:text-left">
            <div className="hidden sm:block shrink-0">
              <SpecialistAvatar size="md" showOnlineStatus={true} bordered={true} />
            </div>
            <div>
              <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-400">Ücretsiz Ön Görüşme</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <h4 className="text-sm sm:text-base font-bold text-white">
                Hangi bölgenin veya seans paketinin kıl yapınıza uygun olduğundan emin değil misiniz?
              </h4>
              <p className="text-xs text-slate-300 mt-0.5">
                Kıl tipi analizi ve size özel seans planlaması için uzmanımızla anında canlı görüşün.
              </p>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2.5 w-full md:w-auto">
            <button
              type="button"
              id="treatment-consult-livechat-btn"
              onClick={() => {
                if (typeof window !== 'undefined' && window.openLiveChat) {
                  window.openLiveChat();
                } else if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('open-live-chat'));
                }
              }}
              className="flex-1 md:flex-none py-2.5 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Uzmana Canlı Danış</span>
            </button>

            <button
              type="button"
              onClick={() => {
                document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex-1 md:flex-none py-2.5 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs border border-white/20 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
            >
              <span>Fiyat Hesaplayıcıya Git</span>
              <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
            </button>
          </div>
        </div>

      </div>

      {/* Area Details Modal */}
      {selectedAreaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-lg w-full p-6 sm:p-7 relative shadow-2xl space-y-4 text-slate-800">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                  {selectedAreaModal.recommendedSessions}
                </span>
                <h3 className="text-xl font-extrabold text-slate-900 mt-2">{selectedAreaModal.name}</h3>
              </div>
              <button
                onClick={() => setSelectedAreaModal(null)}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed">
              {selectedAreaModal.longDesc}
            </p>

            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Bölge Avantajları</h4>
              <ul className="space-y-1.5 text-xs text-slate-700">
                {selectedAreaModal.benefits.map((b, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-xs text-slate-600 bg-amber-50/60 p-3 rounded-xl border border-amber-200">
              <span className="font-bold text-amber-900">Ön Hazırlık: </span>
              {selectedAreaModal.preparationTip}
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setSelectedAreaModal(null);
                  if (typeof window !== 'undefined' && window.openLiveChat) {
                    window.openLiveChat();
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-live-chat'));
                  }
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:brightness-105 text-slate-950 font-black text-xs sm:text-sm text-center flex items-center justify-center gap-2 shadow-sm border border-amber-400 active:scale-95 transition-all cursor-pointer"
              >
                <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} />
                <span>Uzmana Canlı Danış</span>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse ml-0.5" />
              </button>
              <button
                onClick={() => setSelectedAreaModal(null)}
                className="py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 text-xs font-semibold cursor-pointer border border-slate-200 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
